import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useI18n } from '../context/i18nContext';
import { useTheme } from '../context/ThemeContext';
import GoogleLoginButton from '../components/GoogleLoginButton';
import FacebookLoginButton from '../components/FacebookLoginButton';
import AppleLoginButton from '../components/AppleLoginButton';

import Button from '../components/ui/Button';
import '../styles/Auth.css';

const Login = () => {
  // ============================================
  // HOOKS
  // ============================================
  const { t, setLanguage } = useI18n();
  const [searchParams] = useSearchParams();
  const { login, verify2FA, error } = useAuth();
  const { theme, applyServerTheme } = useTheme();
  const bgImage = theme === 'dark'
    ? "url('/la_marina_alta_midnight_blue.webp')"
    : "url('/la_marina_alta_ocean.webp')";
  const navigate = useNavigate();              // Navigáció

  // ============================================
  // STATE VÁLTOZÓK
  // ============================================
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  // 2FA lépés
  const [pendingToken, setPendingToken] = useState(null);
  const [twofaCode, setTwofaCode] = useState('');

  // ?lang= paraméter a landing oldalról — egyszer alkalmazzuk
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang) setLanguage(lang); // setLanguage ignorálja az ismeretlen kódokat
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // SSO config + registration flag
  const [ssoConfig, setSsoConfig] = useState({ sso_google_enabled: false, sso_facebook_enabled: false, sso_apple_enabled: false });
  const [registrationOpen, setRegistrationOpen] = useState(false);

  useEffect(() => {
    fetch('/api/auth/features')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setRegistrationOpen(data.registration_open === true || data.registration_open === 'true'); })
      .catch(() => {});
    fetch('/api/auth/sso-config')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSsoConfig(data); })
      .catch(() => {});
  }, []);

  // ============================================
  // FORM SUBMIT HANDLER
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password, rememberMe);

    if (result.success) {
      if (result.user?.preferred_language) setLanguage(result.user.preferred_language);
      if (result.user?.preferred_theme) applyServerTheme(result.user.preferred_theme);
      navigate('/dashboard');
    } else if (result.requires2FA) {
      // 2. lépés: 2FA kód bekérése
      setPendingToken(result.pendingToken);
    }

    setLoading(false);
  };

  // ============================================
  // 2FA SUBMIT HANDLER
  // ============================================
  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await verify2FA(pendingToken, twofaCode.trim());

    if (result.success) {
      if (result.user?.preferred_language) setLanguage(result.user.preferred_language);
      if (result.user?.preferred_theme) applyServerTheme(result.user.preferred_theme);
      navigate('/dashboard');
    } else if (result.expired) {
      // Lejárt a pending token → vissza az 1. lépéshez
      setPendingToken(null);
      setTwofaCode('');
    }

    setLoading(false);
  };

  // ============================================
  // RENDER — 2FA KÓD KÉPERNYŐ
  // ============================================
  if (pendingToken) {
    return (
      <div className="auth-container" style={{ backgroundImage: bgImage }}>

        <div className="auth-card">
          <h1>{t('auth.twofa.title')}</h1>
          <p className="auth-subtitle">{t('auth.twofa.prompt')}</p>

          {error && (
            <div className="error-message">
              {t(`auth.errors.${error.replace(/\s+/g, '_').toLowerCase()}`, error)}
            </div>
          )}

          <form onSubmit={handle2FASubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="twofaCode">{t('auth.twofa.code')}</label>
              <input
                id="twofaCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={twofaCode}
                onChange={(e) => setTwofaCode(e.target.value)}
                required
                disabled={loading}
                placeholder="123456"
                autoFocus
                className="twofa-code-input"
              />
              <span className="field-hint">{t('auth.twofa.backupHint')}</span>
            </div>

            <Button type="submit" variant="solid" fullWidth disabled={loading || !twofaCode.trim()}>
              {loading ? t('auth.button.loggingIn') : t('auth.twofa.verify')}
            </Button>
          </form>

          <p className="auth-link">
            <button
              type="button"
              className="link-button"
              onClick={() => { setPendingToken(null); setTwofaCode(''); }}
            >
              ← {t('auth.twofa.backToLogin')}
            </button>
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER — LOGIN FORM
  // ============================================
  return (
    <div className="auth-container" style={{ backgroundImage: bgImage }}>
      <div className="language-switcher-top">
        <LanguageSwitcher />
      </div>
      <div className="auth-card">
        <div className="auth-card-header">
          <img src="/villa_presence_concierge_logo.svg" alt="" className="auth-logo-sm" aria-hidden="true" />
          <p className="auth-card-eyebrow">Client Portal</p>
          <h1>{t('auth.login')}</h1>
        </div>

        {/* HIBA ÜZENET */}
        {error && (
          <div className="error-message">
            {t(`auth.errors.${error.replace(/\s+/g, '_').toLowerCase()}`, error)}
          </div>
        )}

        {/* LOGIN FORM */}
        <form onSubmit={handleSubmit} className="auth-form">

          {/* EMAIL INPUT */}
          <div className="form-group">
            <label htmlFor="email">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder={t('auth.email')}
            />
          </div>

          {/* JELSZÓ INPUT — szemecske gombbal */}
          <div className="form-group">
            <label htmlFor="password">
              {t('auth.password')}
            </label>
            <div className="pw-input-wrap">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="pw-eye"
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* EXTRA OPCIÓK */}
          <div className="form-options">
            <label className="remember-me">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <span>{t('auth.rememberMe')}</span>
            </label>
            <Link to="/forgot-password" className="forgot-link">
              {t('auth.forgotPassword')}
            </Link>
          </div>

          {/* LOGIN GOMB */}
          <Button type="submit" variant="solid" fullWidth disabled={loading}>
            {loading ? t('auth.button.loggingIn') : t('auth.button.login')}
          </Button>
        </form>

        {/* SSO GOMBÓK */}
        {(ssoConfig.sso_google_enabled || ssoConfig.sso_facebook_enabled || ssoConfig.sso_apple_enabled) && (
          <>
            <div className="divider"><span>{t('auth.orContinueWith')}</span></div>
            <div className="sso-buttons">
              {ssoConfig.sso_google_enabled   && <GoogleLoginButton />}
              {ssoConfig.sso_facebook_enabled && <FacebookLoginButton />}
              {ssoConfig.sso_apple_enabled    && <AppleLoginButton />}
            </div>
          </>
        )}

        {/* REGISZTRÁCIÓ LINK — csak ha a registration_open be van kapcsolva */}
        {registrationOpen && (
          <p className="auth-link">
            {t('auth.noAccount')}{' '}
            <Link to="/register">
              {t('auth.registerHere')}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
